// Seeded script randomness (SCR-10, ARCH §27.8). PURE (ENG-1).
//
// `random(lo, hi)` / `chance(p)` draw from a generator derived *purely* from
// (entitySeed, tick), where `entitySeed = entityScriptSeed(mobSeed, entityId)` —
// §27.8's `fork(mobSeed, entityId)`. The generator is **re-derived every dispatch
// and never stored**: there is no cursor to save, nothing to rehydrate, nothing a
// hot reload disturbs, and nothing to drift between server and replay (E-42 keeps
// `scriptMem` across a reload; randomness deliberately has no state to keep).
//
// Callers therefore hold a `ScriptRng` for exactly one dispatch and drop it. This
// module holds NO module-level mutable state — both exports are pure functions of
// their arguments, so there is nowhere for a stream cursor to hide.
//
// The PRNG and the seed forking are ENG-3's (`engine/rng.ts`: xoshiro128** driven
// by splitmix64 seeding, `forkAt` for positional sub-streams); no hash and no PRNG
// is re-implemented here. `Math.random` stays lint-banned under src/sim (ENG-3).
import { Rng, defineSalt } from '../engine/rng';

// Fork salts (E-25 — declared once, globally unique).
const S_ENTITY = defineSalt('script-entity');
const S_DISPATCH = defineSalt('script-dispatch');

/** The per-dispatch random surface behind the `random`/`chance` verbs (§27.2). */
export interface ScriptRng {
  /** Inclusive integer in [lo, hi]. lo > hi swaps. */
  random(lo: number, hi: number): number;
  /** True with probability p (p <= 0 always false, p >= 1 always true). */
  chance(p: number): boolean;
}

/** The entity-seed derivation callers use before `scriptRng`: §27.8's
 *  `fork(mobSeed, entityId)`, as a u32 an entity can carry as a plain number.
 *  Pure — the same pair always yields the same seed, on every host. */
export function entityScriptSeed(mobSeed: number, entityId: number): number {
  return new Rng(mobSeed).forkAt(S_ENTITY, entityId, 0).nextU32();
}

/** Per-dispatch RNG. `entitySeed = fork(mobSeed, entityId)`; re-derived per
 *  dispatch, NEVER stored.
 *
 *  Identical (entitySeed, tick) ⇒ an identical draw sequence; a different tick ⇒
 *  a different sequence. Construction makes no draw, so building a generator a
 *  dispatch never uses costs nothing and changes nothing. `forkAt` truncates its
 *  positional slot to int32, so a tick past 2^31 wraps — the same convention
 *  worldgen coordinates already run on — and a non-finite tick lands on 0
 *  (deterministic, never NaN). */
export function scriptRng(entitySeed: number, tick: number): ScriptRng {
  // splitmix64-mixed (entitySeed, salt, tick) → a fresh xoshiro128** stream.
  const stream = new Rng(entitySeed).forkAt(S_DISPATCH, tick, 0);
  // Every verb below draws exactly ONE u32, so how far the stream advances is a
  // function of the script's control flow alone, never of its arguments.
  return Object.freeze({
    random(lo: number, hi: number): number {
      const a = bound(lo), b = bound(hi);
      const min = Math.min(a, b), max = Math.max(a, b); // lo > hi swaps
      // Span ≥ 1 always, so random(5, 5) draws once and returns 5.
      return min + stream.nextInt(max - min + 1);
    },
    chance(p: number): boolean {
      // nextFloat() ∈ [0, 1): p ≤ 0 can never pass and p ≥ 1 always does, with no
      // special case. NaN compares false, so the verb is total (no NaN leak).
      return stream.nextFloat() < p;
    },
  });
}

/** Coerce a `random` bound to a safe integer — floor, matching `api.ts`'s `int()`
 *  coercion, and clamped so `max - min + 1` stays finite (total: a non-finite
 *  bound becomes 0, never a NaN or an Infinity span). */
function bound(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(Number.MAX_SAFE_INTEGER, Math.max(-Number.MAX_SAFE_INTEGER, Math.floor(n)));
}
